import { type FC } from 'react';
import Background from '../../../assets/RobotAvatarBackground.png';
import styled from 'styled-components';
import { type CyberIQLevel, useCyberIQLevelsQuery } from '../../../apiHooks/useCyberIQLevelsQuery';

type RobotAvatarProps = {
  levelId: string | undefined;
  fillContainer?: boolean;
};

const levelToIconMapper: Record<number, { top: string; left: string }> = {
  1: { top: '-5px', left: '-30px' },
  2: { top: '-20px', left: '-40px' },
  3: { top: '-20px', left: '-40px' },
  4: { top: '-20px', left: '-30px' },
  5: { top: '-20px', left: '-40px' },
};

export const RobotAvatar: FC<RobotAvatarProps> = ({ levelId, fillContainer = false }: RobotAvatarProps) => {
  const cyberIQLevelsQuery = useCyberIQLevelsQuery();
  const levels: CyberIQLevel[] = cyberIQLevelsQuery.data ?? [];
  const levelIndex: number = levels.findIndex((level: CyberIQLevel) => {
    return level.id === levelId;
  });
  const level: CyberIQLevel | undefined = levels[levelIndex];

  return (
    <Container $fillContainer={fillContainer}>
      <ImageBackground src={Background} $fillContainer={fillContainer}></ImageBackground>
      {level ? (
        <ImageAvatar
          src={level.assetUrl}
          alt="Avatar"
          top={levelToIconMapper[levelIndex + 1]?.top || '0px'}
          left={levelToIconMapper[levelIndex + 1]?.left || '0px'}
          $fillContainer={fillContainer}
        />
      ) : (
        <NoAvatarMessage>No milestone passed yet</NoAvatarMessage>
      )}
    </Container>
  );
};

const Container = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$fillContainer',
})<{ $fillContainer: boolean }>`
  width: ${({ $fillContainer }) => ($fillContainer ? '100%' : '160px')};
  height: ${({ $fillContainer }) => ($fillContainer ? '100%' : '130px')};
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    width: ${({ $fillContainer }) => ($fillContainer ? '100%' : '150px')};
  }
`;

const ImageBackground = styled.img.withConfig({
  shouldForwardProp: (prop) => prop !== '$fillContainer',
})<{ $fillContainer?: boolean }>`
  position: absolute;
  width: ${({ $fillContainer }) => ($fillContainer ? '100%' : 'calc(100% - 30px)')};
  height: 100%;
  top: 0;
  left: 0;
  display: block;

  @media (max-width: 768px) {
    width: ${({ $fillContainer }) => ($fillContainer ? '100%' : 'calc(100% - 20px)')};
  }
`;

const ImageAvatar = styled.img.withConfig({
  shouldForwardProp: (prop) => prop !== 'top' && prop !== 'left' && prop !== '$fillContainer',
})<{ top: string; left: string; $fillContainer: boolean }>`
  position: absolute;
  width: ${({ $fillContainer }) => ($fillContainer ? 'calc(100% - 8px)' : '200px')};
  height: ${({ $fillContainer }) => ($fillContainer ? 'calc(100% - 8px)' : '200px')};
  top: ${({ top, $fillContainer }) => ($fillContainer ? '56%' : top)};
  left: ${({ left, $fillContainer }) => ($fillContainer ? '50%' : left)};
  transform: ${({ $fillContainer }) => ($fillContainer ? 'translate(-50%, -50%)' : 'none')};
  object-fit: contain;
`;

const NoAvatarMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
  color: #555;
  text-align: center;
`;
